import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaHospital,
  FaEdit,
  FaEye,
  FaSync,
  FaPlus,
  FaTrash,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaUserMd,
  FaStethoscope,
  FaBriefcaseMedical,
  FaBed,
  FaAmbulance,
  FaCalendarCheck,
  FaCheckCircle,
  FaTimesCircle,
  FaSave,
  FaTimes,
  FaSearch,
  FaUser,
  FaGraduationCap,
  FaCalendarAlt,
  FaList,
  FaInfoCircle,
  FaExclamationTriangle,
  FaUpload,
  FaImage
} from 'react-icons/fa';

import { database, storage } from '../firebase';
import { ref as dbRef, get, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AdminHospitalDetails = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [addingDoctor, setAddingDoctor] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    name: '',
    qualification: '',
    specialization: '',
    experience: '',
    selectedDays: [],
    startTime: '09:00',
    endTime: '17:00',
    image: null,
    imagePreview: ''
  });
  const [hospitalData, setHospitalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newDisease, setNewDisease] = useState({ name: '', description: '' });
  const [newService, setNewService] = useState({ name: '', description: '', icon: 'FaUser' });
  const [editingService, setEditingService] = useState(null);
  const [hospitalImage, setHospitalImage] = useState(null);
  const [hospitalImagePreview, setHospitalImagePreview] = useState('');

  // Fetch hospital data from Firebase
  useEffect(() => {
    const fetchHospitalData = async () => {
      try {
        const hospitalRef = dbRef(database, 'Dr/hospital');
        const snapshot = await get(hospitalRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          setHospitalData({
            ...data,
            diseasesTreated: data.diseasesTreated || [],
            doctors: data.doctors || [],
            services: data.services || [],
            facilities: data.facilities || [],
            workingHours: data.workingHours || { mondayToFriday: '', saturday: '', sunday: '' },
            appointmentSettings: data.appointmentSettings || { contactNumber: '', email: '', appointmentEnabled: false },
            status: data.status || { hospitalStatus: 'Active', showOnHomePage: true }
          });
        } else {
          // Initialize with empty structure - all data from user input
          setHospitalData({
            name: '',
            image: '',
            address: '',
            contactNumber: '',
            email: '',
            workingHours: {
              mondayToFriday: '',
              saturday: '',
              sunday: ''
            },
            emergencyAvailable: false,
            about: '',
            vision: '',
            diseasesTreated: [],
            doctors: [],
            services: [],
            facilities: [],
            appointmentSettings: {
              contactNumber: '',
              email: '',
              appointmentEnabled: false
            },
            status: {
              hospitalStatus: 'Active',
              showOnHomePage: true
            }
          });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching hospital data:', error);
        setErrorMessage('Failed to load hospital data');
        setLoading(false);
      }
    };

    fetchHospitalData();
  }, []);

  // Handle basic info changes
  const handleBasicInfoChange = (field, value) => {
    setHospitalData(prev => ({ ...prev, [field]: value }));
  };

  // Handle input changes for nested objects
  const handleInputChange = (section, field, value) => {
    setHospitalData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Handle hospital image upload
  const handleHospitalImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHospitalImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setHospitalImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Add new disease
  const handleAddDisease = () => {
    if (newDisease.name.trim()) {
      setHospitalData(prev => ({
        ...prev,
        diseasesTreated: [...prev.diseasesTreated, {
          id: Date.now(),
          name: newDisease.name,
          description: newDisease.description
        }]
      }));
      setNewDisease({ name: '', description: '' });
    }
  };

  // Remove disease
  const handleRemoveDisease = (id) => {
    setHospitalData(prev => ({
      ...prev,
      diseasesTreated: prev.diseasesTreated.filter(d => d.id !== id)
    }));
  };

  // Add new service
  const handleAddService = () => {
    if (newService.name.trim()) {
      setHospitalData(prev => ({
        ...prev,
        services: [...prev.services, {
          id: Date.now(),
          name: newService.name,
          description: newService.description,
          icon: newService.icon
        }]
      }));
      setNewService({ name: '', description: '', icon: 'FaUser' });
    }
  };

  // Remove service
  const handleRemoveService = (id) => {
    setHospitalData(prev => ({
      ...prev,
      services: prev.services.filter(s => s.id !== id)
    }));
  };

  // Handle doctor image upload in modal
  const handleDoctorImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewDoctor(prev => ({ ...prev, imagePreview: reader.result }));
      };
      reader.readAsDataURL(file);
      setNewDoctor(prev => ({ ...prev, image: file }));
    }
  };

  // Toggle day selection for doctor
  const toggleDaySelection = (day) => {
    setNewDoctor(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day]
    }));
  };

  // Add or update doctor
  const handleSaveDoctor = async () => {
    // Validation
    if (!newDoctor.name.trim()) {
      setErrorMessage('Please enter doctor name');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    if (!newDoctor.qualification.trim()) {
      setErrorMessage('Please enter qualification');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    if (newDoctor.selectedDays.length === 0) {
      setErrorMessage('Please select at least one day');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      let imageUrl = editingDoctor ? editingDoctor.image : '';
      if (newDoctor.image) {
        const imageRef = storageRef(storage, `Dr/hospital/doctors/${Date.now()}.jpg`);
        await uploadBytes(imageRef, newDoctor.image);
        imageUrl = await getDownloadURL(imageRef);
      }

      const doctorData = {
        id: editingDoctor ? editingDoctor.id : Date.now(),
        name: newDoctor.name,
        qualification: newDoctor.qualification,
        specialization: newDoctor.specialization,
        experience: newDoctor.experience,
        availability: newDoctor.selectedDays,
        schedule: `${newDoctor.startTime} - ${newDoctor.endTime}`,
        image: imageUrl
      };

      setHospitalData(prev => ({
        ...prev,
        doctors: editingDoctor
          ? prev.doctors.map(d => d.id === editingDoctor.id ? doctorData : d)
          : [...prev.doctors, doctorData]
      }));

      setAddingDoctor(false);
      setEditingDoctor(null);
      setNewDoctor({
        name: '',
        qualification: '',
        specialization: '',
        experience: '',
        selectedDays: [],
        startTime: '09:00',
        endTime: '17:00',
        image: null,
        imagePreview: ''
      });
      setSuccessMessage('Doctor saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving doctor:', error);
      setErrorMessage('Failed to save doctor');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Remove doctor
  const handleRemoveDoctor = (id) => {
    setHospitalData(prev => ({
      ...prev,
      doctors: prev.doctors.filter(d => d.id !== id)
    }));
  };

  // Save all data to Firebase
  const handleSave = async () => {
    try {
      let imageUrl = hospitalData.image;
      if (hospitalImage) {
        const imageRef = storageRef(storage, 'Dr/hospital/cover.jpg');
        await uploadBytes(imageRef, hospitalImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      const dataToSave = {
        ...hospitalData,
        image: imageUrl
      };

      const hospitalRef = dbRef(database, 'Dr/hospital');
      await set(hospitalRef, dataToSave);

      setIsEditing(false);
      setSuccessMessage('Hospital details updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving hospital data:', error);
      setErrorMessage('Failed to save hospital data');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    navigate('/admin/hospitals');
  };

  // Get Icon component
  const getIconComponent = (iconName) => {
    const icons = {
      FaUser: <FaUser />,
      FaAmbulance: <FaAmbulance />,
      FaStethoscope: <FaStethoscope />,
      FaBriefcaseMedical: <FaBriefcaseMedical />,
      FaBed: <FaBed />
    };
    return icons[iconName] || <FaUser />;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!hospitalData) {
    return <div>No hospital data found</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* PAGE HEADER */}
        <div className="mb-8">
          {/* <nav className="flex text-sm text-gray-600 mb-4" aria-label="Breadcrumb">
            <Link to="/admin/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/admin/hospitals" className="hover:text-teal-600">Manage Hospitals</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800 font-medium">Hospital Details</span>
          </nav> */}
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Hospital Details</h1>
              <p className="text-gray-600 mt-2">
                Manage and view comprehensive details of {hospitalData.name}
              </p>
            </div>
            
            <div className="mt-4 sm:mt-0">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <FaHospital className="text-teal-500" />
                <span>Multi-specialty Healthcare Facility</span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
            <div className="flex items-center">
              <FaCheckCircle className="text-green-500 text-xl mr-3" />
              <p className="font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <FaTimesCircle className="text-red-500 text-xl mr-3" />
              <p className="font-medium text-red-800">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* TOP ACTION BAR */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <FaHospital className="mr-2 text-teal-600" />
                Hospital Control Panel
              </h2>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                hospitalData.status.hospitalStatus === 'Active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {hospitalData.status.hospitalStatus}
              </span>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`px-5 py-2.5 font-medium rounded-xl flex items-center transition-all ${
                  isEditing
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700'
                }`}
              >
                <FaEdit className="mr-2" />
                {isEditing ? 'Cancel Editing' : 'Edit Hospital Details'}
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors flex items-center"
              >
                <FaSync className="mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* HOSPITAL BASIC INFORMATION SECTION */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h2 className="text-xl font-bold text-gray-800">Basic Information</h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Image and Address */}
                <div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Hospital Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden">
                      <img
                        src={hospitalImagePreview || hospitalData.image}
                        alt={hospitalData.name}
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    {isEditing && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleHospitalImageUpload}
                        className="mt-2"
                      />
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                        Address
                      </label>
                      {isEditing ? (
                        <textarea
                          value={hospitalData.address}
                          onChange={(e) => handleBasicInfoChange('address', e.target.value)}
                          rows="3"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      ) : (
                        <p className="text-gray-700 p-4 bg-gray-50 rounded-xl">{hospitalData.address}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emergency Available
                      </label>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => handleBasicInfoChange('emergencyAvailable', !hospitalData.emergencyAvailable)}
                          disabled={!isEditing}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                            hospitalData.emergencyAvailable ? 'bg-red-600' : 'bg-gray-300'
                          } ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            hospitalData.emergencyAvailable ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                        <span className={`text-sm font-medium ${
                          hospitalData.emergencyAvailable ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {hospitalData.emergencyAvailable ? 'Available 24x7' : 'Not Available'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Column - Contact Details */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hospital Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={hospitalData.name}
                        onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                      />
                    ) : (
                      <h3 className="text-2xl font-bold text-gray-800">{hospitalData.name}</h3>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaPhone className="inline mr-2 text-gray-400" />
                        Contact Number
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={hospitalData.contactNumber}
                          onChange={(e) => handleBasicInfoChange('contactNumber', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                        />
                      ) : (
                        <p className="p-3 bg-gray-50 rounded-xl text-gray-700">{hospitalData.contactNumber}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaEnvelope className="inline mr-2 text-gray-400" />
                        Email ID
                      </label>
                      {isEditing ? (
                        <input
                          type="email"
                          value={hospitalData.email}
                          onChange={(e) => handleBasicInfoChange('email', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                        />
                      ) : (
                        <p className="p-3 bg-gray-50 rounded-xl text-gray-700">{hospitalData.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FaClock className="inline mr-2 text-gray-400" />
                      Working Hours
                    </label>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monday - Friday:</span>
                        {isEditing ? (
                          <input
                            type="text"
                            value={hospitalData.workingHours.mondayToFriday}
                            onChange={(e) => handleInputChange('workingHours', 'mondayToFriday', e.target.value)}
                            className="w-48 p-2 border border-gray-300 rounded-lg"
                          />
                        ) : (
                          <span className="font-medium">{hospitalData.workingHours.mondayToFriday}</span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Saturday:</span>
                        {isEditing ? (
                          <input
                            type="text"
                            value={hospitalData.workingHours.saturday}
                            onChange={(e) => handleInputChange('workingHours', 'saturday', e.target.value)}
                            className="w-48 p-2 border border-gray-300 rounded-lg"
                          />
                        ) : (
                          <span className="font-medium">{hospitalData.workingHours.saturday}</span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sunday:</span>
                        {isEditing ? (
                          <input
                            type="text"
                            value={hospitalData.workingHours.sunday}
                            onChange={(e) => handleInputChange('workingHours', 'sunday', e.target.value)}
                            className="w-48 p-2 border border-gray-300 rounded-lg"
                          />
                        ) : (
                          <span className="font-medium">{hospitalData.workingHours.sunday}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* HOSPITAL DESCRIPTION / ABOUT SECTION */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-emerald-50">
              <h2 className="text-xl font-bold text-gray-800">Hospital Description</h2>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    About Hospital
                  </label>
                  {isEditing ? (
                    <textarea
                      value={hospitalData.about}
                      onChange={(e) => handleBasicInfoChange('about', e.target.value)}
                      rows="4"
                      className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    />
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-gray-700 whitespace-pre-line">{hospitalData.about}</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vision / Mission (Optional)
                  </label>
                  {isEditing ? (
                    <textarea
                      value={hospitalData.vision}
                      onChange={(e) => handleBasicInfoChange('vision', e.target.value)}
                      rows="3"
                      className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                      placeholder="Enter hospital vision and mission..."
                    />
                  ) : (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                      <p className="text-gray-700 italic">"{hospitalData.vision}"</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* DISEASES TREATED SECTION */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <FaStethoscope className="mr-2 text-purple-600" />
                  Diseases Treated
                </h2>
                {isEditing && (
                  <button
                    onClick={() => setNewDisease({ name: '', description: '' })}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center"
                  >
                    <FaPlus className="mr-2" />
                    Add Disease
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              {/* Add Disease Form */}
              {isEditing && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <h3 className="font-medium text-gray-700 mb-3">Add New Disease</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={newDisease.name}
                      onChange={(e) => setNewDisease({ ...newDisease, name: e.target.value })}
                      placeholder="Disease Name"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="text"
                      value={newDisease.description}
                      onChange={(e) => setNewDisease({ ...newDisease, description: e.target.value })}
                      placeholder="Description (Optional)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={handleAddDisease}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Diseases List */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hospitalData.diseasesTreated.map((disease) => (
                  <div key={disease.id} className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-gray-800 text-lg mb-1">{disease.name}</h4>
                      <p className="text-sm text-gray-600">{disease.description}</p>
                    </div>
                    {isEditing && (
                      <button
                        onClick={() => handleRemoveDisease(disease.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DOCTOR INFORMATION SECTION */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-yellow-50">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <FaUserMd className="mr-2 text-amber-600" />
                  Doctor Information
                </h2>
                {isEditing && (
                  <button
                    onClick={() => setAddingDoctor(true)}
                    className="px-4 py-2 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-medium rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-colors flex items-center"
                  >
                    <FaPlus className="mr-2" />
                    Add Doctor
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hospitalData.doctors.map((doctor) => (
                  <div key={doctor.id} className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 p-5">
                    <div className="flex items-start space-x-4 mb-4">
                      <img
                        src={doctor.image}
                        alt={doctor.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-amber-200"
                      />
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg">{doctor.name}</h3>
                        <p className="text-sm text-gray-600">{doctor.qualification}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><strong>Specialization:</strong> {doctor.specialization || 'N/A'}</p>
                      <p><strong>Experience:</strong> {doctor.experience}</p>
                      <p><strong>Availability:</strong> {Array.isArray(doctor.availability) ? doctor.availability.join(', ') : doctor.availability}</p>
                      <p><strong>Schedule:</strong> {doctor.schedule}</p>
                    </div>
                    {isEditing && (
                      <div className="mt-4 flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingDoctor(doctor);
                            // Parse schedule to extract start and end time
                            const scheduleParts = doctor.schedule.split(' - ');
                            setNewDoctor({
                              name: doctor.name,
                              qualification: doctor.qualification,
                              specialization: doctor.specialization || '',
                              experience: doctor.experience,
                              selectedDays: doctor.availability || [],
                              startTime: scheduleParts[0] || '09:00',
                              endTime: scheduleParts[1] || '17:00',
                              image: null,
                              imagePreview: doctor.image
                            });
                            setAddingDoctor(true);
                          }}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all"
                        >
                          <FaEdit className="inline mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveDoctor(doctor.id)}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                        >
                          <FaTrash className="inline mr-1" />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SERVICES & FACILITIES SECTION */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <FaBriefcaseMedical className="mr-2 text-green-600" />
                  Services & Facilities
                </h2>
                {isEditing && (
                  <button
                    onClick={() => setNewService({ name: '', description: '', icon: 'FaUser' })}
                    className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-xl hover:from-green-700 hover:to-emerald-700 flex items-center"
                  >
                    <FaPlus className="mr-2" />
                    Add Service
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              {/* Add Service Form */}
              {isEditing && (
                <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                  <h3 className="font-medium text-gray-700 mb-3">Add New Service</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      value={newService.name}
                      onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                      placeholder="Service Name"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <input
                      type="text"
                      value={newService.description}
                      onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                      placeholder="Description"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <select
                      value={newService.icon}
                      onChange={(e) => setNewService({ ...newService, icon: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="FaUser">OPD</option>
                      <option value="FaAmbulance">Emergency</option>
                      <option value="FaStethoscope">Consultation</option>
                      <option value="FaBriefcaseMedical">Diet</option>
                      <option value="FaBed">Health Checkup</option>
                    </select>
                  </div>
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={handleAddService}
                      className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Services List */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {hospitalData.services.map((service) => (
                  <div key={service.id} className="p-4 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 flex items-start space-x-3">
                    {getIconComponent(service.icon)}
                    <div>
                      <h4 className="font-bold text-gray-800">{service.name}</h4>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                    {isEditing && (
                      <button
                        onClick={() => handleRemoveService(service.id)}
                        className="ml-auto p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Facilities */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Available Facilities</h3>
                <div className="flex flex-wrap gap-2">
                  {hospitalData.facilities.map((facility, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 rounded-full text-sm"
                    >
                      {facility}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CONTACT & APPOINTMENT SETTINGS */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-pink-50 to-rose-50">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <FaCalendarCheck className="mr-2 text-pink-600" />
                Contact & Appointment Settings
              </h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appointment Contact Number
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={hospitalData.appointmentSettings.contactNumber}
                      onChange={(e) => handleInputChange('appointmentSettings', 'contactNumber', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    />
                  ) : (
                    <p className="p-3 bg-gray-50 rounded-xl text-gray-700">{hospitalData.appointmentSettings.contactNumber}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appointment Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={hospitalData.appointmentSettings.email}
                      onChange={(e) => handleInputChange('appointmentSettings', 'email', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    />
                  ) : (
                    <p className="p-3 bg-gray-50 rounded-xl text-gray-700">{hospitalData.appointmentSettings.email}</p>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Appointment Booking Status
                </label>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('appointmentSettings', 'appointmentEnabled', !hospitalData.appointmentSettings.appointmentEnabled)}
                    disabled={!isEditing}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                      hospitalData.appointmentSettings.appointmentEnabled ? 'bg-green-600' : 'bg-gray-300'
                    } ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      hospitalData.appointmentSettings.appointmentEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className={`text-sm font-medium ${
                    hospitalData.appointmentSettings.appointmentEnabled ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {hospitalData.appointmentSettings.appointmentEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* STATUS & VISIBILITY SETTINGS */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <FaInfoCircle className="mr-2 text-indigo-600" />
                Status & Visibility Settings
              </h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hospital Status
                  </label>
                  {isEditing ? (
                    <select
                      value={hospitalData.status.hospitalStatus}
                      onChange={(e) => handleInputChange('status', 'hospitalStatus', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  ) : (
                    <p className="p-3 bg-gray-50 rounded-xl text-gray-700">{hospitalData.status.hospitalStatus}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Show on Home Page
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => handleInputChange('status', 'showOnHomePage', !hospitalData.status.showOnHomePage)}
                      disabled={!isEditing}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                        hospitalData.status.showOnHomePage ? 'bg-indigo-600' : 'bg-gray-300'
                      } ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        hospitalData.status.showOnHomePage ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <span className={`text-sm font-medium ${
                      hospitalData.status.showOnHomePage ? 'text-indigo-600' : 'text-gray-500'
                    }`}>
                      {hospitalData.status.showOnHomePage ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SAVE ACTIONS */}
          {isEditing && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                <div>
                  <h3 className="font-medium text-gray-700">Ready to save changes?</h3>
                  <p className="text-sm text-gray-500">Review all information before updating</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-medium rounded-xl hover:from-teal-700 hover:to-emerald-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ADD/EDIT DOCTOR MODAL */}
      {(addingDoctor || editingDoctor) && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-yellow-500 px-8 py-6 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  <FaUserMd className="mr-3 text-3xl" />
                  {editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}
                </h3>
                <button
                  onClick={() => {
                    setAddingDoctor(false);
                    setEditingDoctor(null);
                    setNewDoctor({
                      name: '',
                      qualification: '',
                      specialization: '',
                      experience: '',
                      selectedDays: [],
                      startTime: '09:00',
                      endTime: '17:00',
                      image: null,
                      imagePreview: ''
                    });
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
                >
                  <FaTimes className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 space-y-6">
              {/* Doctor Image Upload */}
              <div className="flex flex-col items-center mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <FaImage className="inline mr-2" />
                  Doctor Photo
                </label>
                <div className="relative">
                  {newDoctor.imagePreview ? (
                    <img
                      src={newDoctor.imagePreview}
                      alt="Preview"
                      className="w-32 h-32 rounded-full object-cover border-4 border-amber-200 shadow-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center border-4 border-gray-300">
                      <FaUserMd className="text-5xl text-gray-400" />
                    </div>
                  )}
                  <label className="absolute bottom-0 right-0 bg-amber-500 hover:bg-amber-600 text-white rounded-full p-3 cursor-pointer shadow-lg transition-all">
                    <FaUpload />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleDoctorImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FaUser className="inline mr-2 text-amber-500" />
                    Doctor Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Dr. John Doe"
                    value={newDoctor.name}
                    onChange={(e) => setNewDoctor({ ...newDoctor, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FaGraduationCap className="inline mr-2 text-amber-500" />
                    Qualification *
                  </label>
                  <input
                    type="text"
                    placeholder="MBBS, MD"
                    value={newDoctor.qualification}
                    onChange={(e) => setNewDoctor({ ...newDoctor, qualification: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FaStethoscope className="inline mr-2 text-amber-500" />
                    Specialization
                  </label>
                  <input
                    type="text"
                    placeholder="Cardiologist, Surgeon, etc."
                    value={newDoctor.specialization}
                    onChange={(e) => setNewDoctor({ ...newDoctor, specialization: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FaBriefcaseMedical className="inline mr-2 text-amber-500" />
                    Experience
                  </label>
                  <input
                    type="text"
                    placeholder="10 years"
                    value={newDoctor.experience}
                    onChange={(e) => setNewDoctor({ ...newDoctor, experience: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  />
                </div>
              </div>

              {/* Availability Days */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <FaCalendarAlt className="inline mr-2 text-amber-500" />
                  Available Days *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDaySelection(day)}
                      className={`px-4 py-3 rounded-xl font-medium transition-all ${
                        newDoctor.selectedDays.includes(day)
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md transform scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
                {newDoctor.selectedDays.length > 0 && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: <span className="font-semibold text-amber-600">{newDoctor.selectedDays.join(', ')}</span>
                  </p>
                )}
              </div>

              {/* Time Schedule */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <FaClock className="inline mr-2 text-amber-500" />
                  Working Hours
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Start Time</label>
                    <input
                      type="time"
                      value={newDoctor.startTime}
                      onChange={(e) => setNewDoctor({ ...newDoctor, startTime: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">End Time</label>
                    <input
                      type="time"
                      value={newDoctor.endTime}
                      onChange={(e) => setNewDoctor({ ...newDoctor, endTime: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    />
                  </div>
                </div>
                <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-gray-700">
                    <FaClock className="inline mr-2 text-amber-600" />
                    Schedule: <span className="font-bold text-amber-700">{newDoctor.startTime} - {newDoctor.endTime}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200">
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setAddingDoctor(false);
                    setEditingDoctor(null);
                    setNewDoctor({
                      name: '',
                      qualification: '',
                      specialization: '',
                      experience: '',
                      selectedDays: [],
                      startTime: '09:00',
                      endTime: '17:00',
                      image: null,
                      imagePreview: ''
                    });
                  }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all flex items-center"
                >
                  <FaTimes className="mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveDoctor}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-yellow-600 shadow-lg transition-all flex items-center"
                >
                  <FaSave className="mr-2" />
                  {editingDoctor ? 'Update Doctor' : 'Add Doctor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHospitalDetails;

